import { DataFieldTypeEnum } from "abweb/dto/generated/ds/DataFieldTypeEnum";
import { RecordFieldTypeEnum } from "abweb/dto/generated/ds/RecordFieldTypeEnum";
import { DataSourceField } from "abweb/dto/ds/DataSourceField";
import { DataSourceState } from "abweb/dto/generated/ds/DataSourceState";
import { DataSourceValue } from "abweb/dto/ds/DataSourceValue";
import { DataSourceValueBoolean } from "abweb/dto/ds/DataSourceValueBoolean";
import { DataSourceValueNumber } from "abweb/dto/ds/DataSourceValueNumber";
import { DataSourceValueDate } from "abweb/dto/ds/DataSourceValueDate";
import { DataSourceValueString } from "abweb/dto/ds/DataSourceValueString";
import { DataSourceValueVector } from "abweb/dto/ds/DataSourceValueVector";
import { DataSourceReference } from "abweb/dto/ds/DataSourceReference";
import { ReferenceTuple } from "abweb/dto/ds/ReferenceTuple";
import { ClassUtils } from "abweb/utils/ClassUtils";

/**
 * Factory method to create instances of various subclasses of DataSourceValue.
 * It is NOT in DataValue itself as it results in a circular dependency hairball with its child classes (even in SystemJS)
 */
export class DataSourceValueFactory {
  /**
   * Factory method to create a strongly-typed DataSource value from a base type.
   * @param value Base type, eg, string, boolean, Date, DataSourceValue
   * @param refID reference
   */
  static createFromCanonicalType(value, refID = null, required = false) {
    if (ClassUtils.isInstanceOf(value, DataSourceValue)) {
      return value;
    }
    if (value == null) {
      value = "";
    }
    const type = typeof value;
    if (type === "boolean") {
      const field = DataSourceValueFactory.createDataSourceField(DataFieldTypeEnum.BOOLEAN, "Name", required);
      const booleanValue = DataSourceValueBoolean.createDataSourceValueBoolean(null, null, null, null, null, refID, DataSourceState.NORMAL, null, value);
      booleanValue._field = field;
      booleanValue.updateValidationError();
      return booleanValue;
    }
    if (type === "number") {
      const field = DataSourceValueFactory.createDataSourceField(DataFieldTypeEnum.DECIMAL, "Name", required);
      const numberValue = DataSourceValueNumber.createDataSourceValueNumber(null, null, null, null, null, refID, DataSourceState.NORMAL, null, value);
      numberValue._field = field;
      numberValue.updateValidationError();
      return numberValue;
    }
    if (ClassUtils.isInstanceOf(value, Date)) {
      const field = DataSourceValueFactory.createDataSourceField(DataFieldTypeEnum.DATE, "Name", required);
      const dateValue = DataSourceValueDate.createDataSourceValueDate(null, null, null, null, null, refID, DataSourceState.NORMAL, null, value);
      dateValue._field = field;
      dateValue.updateValidationError();
      return dateValue;
    }
    if (ClassUtils.isInstanceOf(value, ReferenceTuple)) {
      const field = DataSourceValueFactory.createDataSourceField(DataFieldTypeEnum.REFERENCE, "Name", required);
      const referenceValue = DataSourceReference.createDataSourceReference(null, null, null, null, null, value.value, DataSourceState.NORMAL, null, null, value.displayValue, value.value);
      referenceValue._field = field;
      referenceValue.updateValidationError();
      return referenceValue;
    }
    if (Array.isArray(value)) {
      return this.createVectorType(value, refID);
    }

    // coerce everything else into a string
    value = value.toString();
    const stringField = DataSourceValueFactory.createDataSourceField(DataFieldTypeEnum.STRING, "Name", required);
    const stringValue = DataSourceValueString.createDataSourceValueString(null, null, null, null, null, refID, DataSourceState.NORMAL, null, value);
    stringValue._field = stringField;
    stringValue.updateValidationError();
    return stringValue;
  }
  static createDataSourceField(fieldType, name, required = false) {
    return DataSourceField.createDataSourceField(
    // So why can't this just be called 'create' ?
    [], null, false, fieldType, true, false, null, name,
    // So why isn't this called 'id' - as is with DataSource{,Record} ?
    true, null, null, required, true, RecordFieldTypeEnum.Base, null);
  }
  static createVectorType(values, refID = null) {
    const dsValues = [];
    let vectorItemType = null;
    let itemType = null;
    if (values != null) {
      values.forEach(val => {
        const dsValue = this.createFromCanonicalType(val);
        if (vectorItemType === null) {
          vectorItemType = dsValue.fieldType;
          itemType = dsValue._field;
        } else {
          if (dsValue.fieldType !== vectorItemType) {
            throw Error("Values within a vector must all be of the same data type.");
          }
        }
        dsValues.push(dsValue);
      });
    }
    const vectorDataSourceValue = DataSourceValueVector.createDataSourceValueVector(null, null, null, refID, dsValues);
    vectorDataSourceValue._field = DataSourceField.createDataSourceField([], null, false, vectorItemType, false, false, itemType, "Name", true, null, null, false, true, RecordFieldTypeEnum.Vector, null);
    return vectorDataSourceValue;
  }
  static createFromField(field) {
    let dsv;
    switch (field.fieldType) {
      case DataFieldTypeEnum.STRING:
      case DataFieldTypeEnum.TEXT:
      default:
        dsv = DataSourceValueString.createWithReadOnlyDefaults(null, null);
        break;
      case DataFieldTypeEnum.DECIMAL:
        dsv = DataSourceValueNumber.createWithReadOnlyDefaults(null, 0);
        break;
      case DataFieldTypeEnum.DATE:
      case DataFieldTypeEnum.DATETIME:
        dsv = DataSourceValueDate.createWithReadOnlyDefaults(null, null);
        break;
      case DataFieldTypeEnum.BOOLEAN:
        dsv = DataSourceValueBoolean.createWithReadOnlyDefaults(null, false);
        break;
      case DataFieldTypeEnum.REFERENCE:
        dsv = DataSourceReference.createWithReadOnlyDefaults(null, null, null, null);
        break;
    }
    dsv._field = field;
    dsv.initializeWithDefaultFieldValue(field);
    return dsv;
  }
}
//# sourceMappingURL=DataSourceValueFactory.js.map
